import React, { useCallback, useState, useMemo } from 'react';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';
import BootstrapTable from 'react-bootstrap-table-next';
import { Row, Col, ListGroupItem, ListGroup } from 'reactstrap';

import ModalConfirmation from 'components/Modal/ConfirmationModal';

import OpportunityService from '../../../../../services/opportunityService';
import { toast } from 'react-toastify';

import './styles.css';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import OpportunityHistoryModal from '../OpportunityHistoryModal';

import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import OpportunityActionsModal from '../../../OpportunityActionsModal';
import ModalOpportunityInfo from '../ModalOpportunityInfo';
import FlexTable from 'components/FlexTable';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';

export const formatTemperature = (temperature) => {
  if (!temperature) return '';
  if (temperature <= 3) return 'Frio';
  if (temperature <= 7) return 'Morno';
  return 'Quente';
};

const ListOpportunitiesTable = ({
  opportunities,
  reloadOpportunities,
  customFields,
  transferOpportunities,
  selectedRows,
  handleOnSelectAll,
  handleOnSelect,
  totalAmount,
  totalOpportunities,
  setSortConfig,
  sortConfig,
  customFieldsTotal = [],
  openModalOpportunityId = null
}) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [opportunityId, setOpportunityId] = useState(null);

  const history = useHistory();

  const handleDeleteOpportunity = useCallback(
    (id) => {
      OpportunityService.delete(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Erro ao deletar a oportunidade';
          toast.error(message);
        })
        .finally(() => {
          reloadOpportunities(1);
          setOpenModalConfirmation(false);
        });
    },
    [reloadOpportunities]
  );

  const editOpportunity = (opportunity) => {
    history.push(
      `/admin/crm/opportunityEdit/${opportunity.recordTypeId}/${opportunity.id}`
    );
  };

  const controlDeleteOpportunity = (id) => {
    setOpportunityId(id);
    setOpenModalConfirmation(true);
  };

  const formattedColumns = useMemo(() => {
    const columns = [];

    columns.push(
      {
        dataField: 'title',
        text: 'Título',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_OPPORTUNITIES_EDIT']) ? (
                <span
                  className="opportunity-name"
                  onClick={() => {
                    editOpportunity(row);
                  }}
                >
                  {row.title || row.name || '-'}
                </span>
              ) : (
                <span>{row.title || row.name || '-'}</span>
              )}
            </>
          );
        }
      },
      {
        dataField: 'stage_name',
        text: 'Fase',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) =>
          row.opportunityStage?.name || row.stageName || '-'
      },
      {
        dataField: 'temperature',
        text: 'Temperatura',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { temperature }) => formatTemperature(temperature)
      },
      {
        dataField: 'created_at',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { createdAt }) => toBrazilianFullDate(createdAt)
      },
      {
        dataField: 'created_by',
        text: 'Criado por',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { createdByName }) => createdByName
      },
      {
        dataField: 'responsible',
        text: 'Responsável',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { responsibleName }) => responsibleName
      },
      {
        dataField: 'amount',
        text: 'Valor',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (!row) return 'R$ 0,00';
          return formatCurrency(row, false);
        }
      },
      {
        dataField: 'last_updated_at',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { lastUpdatedAt }) =>
          toBrazilianFullDate(lastUpdatedAt)
      },
      {
        dataField: 'catalogName',
        text: 'Catálogo de preço',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { catalogName }) => catalogName
      },
      {
        dataField: 'productName',
        text: 'Produto',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { productName }) => productName
      },
      {
        dataField: 'catalogTotalValue',
        text: 'Valor total',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { catalogTotalValue }) => catalogTotalValue
      },
      {
        dataField: 'accountName',
        text: 'Conta',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { accountName }) => accountName
      },
      {
        dataField: 'contactName',
        text: 'Contato',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { contactName }) => contactName
      },
      {
        dataField: 'channelOrigin',
        text: 'Origem da Interação',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { channelOrigin }) => channelOrigin || '-'
      },
      {
        dataField: 'firstContactDate',
        text: 'Data do Primeiro Contato',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { firstContactDate }) =>
          firstContactDate ? toBrazilianFullDate(firstContactDate) : '-'
      },
      {
        dataField: 'isConverted',
        text: 'Gerada via Lead',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { isConverted }) => (isConverted ? 'Sim' : 'Não')
      },
      {
        dataField: 'isManual',
        text: 'Gerada Manualmente',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { isManual }) => (isManual ? 'Sim' : 'Não')
      },
      {
        dataField: 'associatedBy',
        text: 'Operador que Associou',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associatedBy }) => associatedBy || '-'
      },
      {
        dataField: 'associationDate',
        text: 'Data de Associação',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_row, { associationDate }) =>
          associationDate ? toBrazilianFullDate(associationDate) : '-'
      }
    );

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        sort: true,
        style: individual_column_style,
        formatter: (_, row) => {
          if (field.type === 'DATE' && !!row[field.name]) {
            return new Date(row[field.name]).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && row[field.name]) {
            return (
              <PhoneCell
                phone={row[field.name].number}
                ddi={row[field.name].ddi}
              />
            );
          } else if (field.type === 'CPF' && row[field.name]) {
            return maskCpf(row[field.name]);
          } else if (field.type === 'CNPJ' && row[field.name]) {
            return maskCnpj(row[field.name]);
          } else if (field.type === 'CURRENCY' && row[field.name]) {
            return formatCurrency(row[field.name]);
          }
          return row[`${field.name}`];
        }
      });
    });

    columns.push({
      dataField: 'edit',
      text: 'Editar',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['CRM_OPPORTUNITIES_EDIT']) ? (
              <Button
                size="sm"
                color="info"
                onClick={() => editOpportunity(row)}
              >
                <li className="fas fa-user-edit"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    columns.push({
      dataField: 'info',
      text: 'Informações',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <ModalOpportunityInfo
            opportunity={row}
            openAutomatically={openModalOpportunityId === row.id}
          />
        );
      }
    });

    columns.push({
      dataField: 'history',
      text: 'Histórico',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <OpportunityHistoryModal
            opportunityId={row.id}
            convertOpportunityId={row.convertOpportunityId}
          />
        );
      }
    });

    columns.push({
      dataField: 'actions',
      text: 'Ações',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return <OpportunityActionsModal opportunityData={row} />;
      }
    });

    columns.push({
      dataField: 'delete',
      text: 'Excluir',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['CRM_OPPORTUNITIES_DELETE']) ? (
              <Button
                size="sm"
                color="danger"
                onClick={() => controlDeleteOpportunity(row.id)}
              >
                <li className="fas fa-trash"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    return [...columns];
  }, [openModalOpportunityId, customFields]);

  return (
    <>
      <ToolkitProvider
        data={opportunities || []}
        keyField="id"
        columns={formattedColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            {transferOpportunities ? (
              <BootstrapTable
                selectRow={{
                  mode: 'checkbox',
                  selected: selectedRows.map((r) => r.id),
                  onSelect: handleOnSelect,
                  onSelectAll: handleOnSelectAll
                }}
                bootstrap4={true}
                bordered={false}
                classes="align-items-center"
                {...props.baseProps}
              />
            ) : (
              <FlexTable
                {...props.baseProps}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                entityName="opportunity"
              />
            )}
          </div>
        )}
      </ToolkitProvider>
      {opportunities && opportunities.length > 0 && (
        <Row className="mb-3 mt-3">
          <Col>
            <ListGroup flush>
              <ListGroupItem
                disabled
                style={{
                  padding: '15px 20px'
                }}
              >
                <div className="mb-2">
                  <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    Valor Total:
                  </span>{' '}
                  {formatCurrency(totalAmount, false)}
                  <br />
                  <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                    Valor Médio:
                  </span>{' '}
                  {formatCurrency(totalAmount / totalOpportunities, false)}
                </div>

                {customFieldsTotal && customFieldsTotal.length > 0 && (
                  <hr style={{ margin: '10px 0' }} />
                )}

                {customFieldsTotal && customFieldsTotal.length > 0 && (
                  <div
                    style={{
                      columnWidth: '250px',
                      columnGap: '40px'
                    }}
                  >
                    <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                      Campos Customizados:
                    </span>
                    {customFieldsTotal.map((field, index) => (
                      <div
                        key={index}
                        style={{
                          breakInside: 'avoid',
                          pageBreakInside: 'avoid',
                          marginBottom: '5px'
                        }}
                      >
                        <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                          {field.label}:
                        </span>{' '}
                        {field.value}
                      </div>
                    ))}
                  </div>
                )}
              </ListGroupItem>
            </ListGroup>
          </Col>
        </Row>
      )}

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={opportunityId}
        deleteResource={handleDeleteOpportunity}
        message={'Tem certeza que deseja excluir a oportunidade?'}
      />
    </>
  );
};

export default ListOpportunitiesTable;
